// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import type { BenderProfile } from '@evolvemep/api-ts';

import { PillSelect } from 'components/Mantine/PillSelect';
import { isNil, isNotNil } from 'helpers/isNotNil';
import {
  extractConduitSizes,
  extractConduitTypes,
} from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/helpers';
import { useBendCreatorFormContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';
import { useConduits } from 'modules/Shop/EvolveFabrication/useConduits';

export const BendConduitSelection = ({
  specificProfile,
  loadingSpecificProfile,
}: {
  specificProfile: BenderProfile | undefined;
  loadingSpecificProfile: boolean;
}) => {
  const form = useBendCreatorFormContext();
  const { conduitTypes, conduitSizes } = useConduits();

  const validConduitTypes = useMemo(
    () =>
      conduitTypes?.filter(({ conduitTypeId }) =>
        isNotNil(specificProfile) ? extractConduitTypes(specificProfile).includes(conduitTypeId) : true,
      ),
    [conduitTypes, specificProfile],
  );

  const {
    values: { conduitTypeId: currentConduitTypeId },
  } = form;
  const validConduitSizes = useMemo(
    () =>
      conduitSizes?.filter(({ conduitSizeId }) =>
        isNotNil(specificProfile)
          ? isNotNil(currentConduitTypeId) &&
            extractConduitSizes(specificProfile, currentConduitTypeId).includes(conduitSizeId)
          : true,
      ),
    [conduitSizes, currentConduitTypeId, specificProfile],
  );

  const { wrappedGetInputProps, validateBend, resetLastValidation } = useBendCreatorValidationContext();

  return (
    <>
      <PillSelect
        label="Conduit Type"
        data={validConduitTypes?.map(({ conduitTypeId, name }) => ({ label: name, value: conduitTypeId })) ?? []}
        required
        disabled={isNil(form.values.benderProfile) || loadingSpecificProfile}
        description={isNil(form.values.benderProfile) ? 'Select a bender to see conduit types' : undefined}
        {...wrappedGetInputProps('conduitTypeId', { validateOnChange: true })}
        onChange={(conduitTypeId) => {
          form.setFieldValue('conduitTypeId', conduitTypeId);
          if (isNil(conduitTypeId)) return;
          if (
            isNotNil(specificProfile) &&
            isNotNil(form.values.conduitSizeId) &&
            !extractConduitSizes(specificProfile, conduitTypeId).includes(form.values.conduitSizeId)
          ) {
            form.setFieldValue('conduitSizeId', null);
            resetLastValidation();
            return;
          }
          void validateBend('conduitTypeId', conduitTypeId);
        }}
      />
      <PillSelect
        label="Conduit Size"
        data={validConduitSizes?.map(({ conduitSizeId, name }) => ({ label: name, value: conduitSizeId })) ?? []}
        required
        mono
        perRow={5}
        disabled={isNil(form.values.benderProfile) || isNil(form.values.conduitTypeId) || loadingSpecificProfile}
        description={
          isNil(form.values.benderProfile)
            ? 'Select a bender to see conduit sizes'
            : isNil(form.values.conduitTypeId)
              ? 'Select a conduit type to see conduit sizes'
              : undefined
        }
        {...wrappedGetInputProps('conduitSizeId', { validateOnChange: true })}
      />
    </>
  );
};
